import { motion } from 'framer-motion';
import { 
  GraduationCap,
  Landmark,
  Archive,
  Wrench,
  PackageSearch,
  DollarSign,
  Code,
  Dice5,
  Network,
  Puzzle,
  BookOpen
} from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { cn } from "@/lib/utils";

const solutions = [
  {
    icon: <GraduationCap className="h-8 w-8 text-primary" />,
    href: "/blockchain-training-enterprise-engineers",
    title: "Blockchain Training for Enterprise Engineers",
    description: "Tailored training programs designed to upskill engineers for implementing and managing blockchain technologies within enterprise environments.",
  },
  {
    icon: <Landmark className="h-8 w-8 text-primary" />,
    href: "/financial-trading-tokenization",
    title: "Financial, Trading, and Tokenization",
    description: "End-to-end blockchain solutions designed for secure financial transactions, asset tokenization, and trading platforms.",
  },
  {
    icon: <Archive className="h-8 w-8 text-primary" />,
    href: "/archival-recordkeeping",
    title: "Archival and Recordkeeping",
    description: "From property deeds to death records, our offerings are designed to stand the test of time.",
  },
  {
    icon: <Wrench className="h-8 w-8 text-primary" />,
    href: "/skilled-industries",
    title: "Skilled Industries",
    description: "From digital logbooks for professional divers to tracing car parts on the assembly line, we got you covered!",
  },
  {
    icon: <PackageSearch className="h-8 w-8 text-primary" />,
    href: "/provenance",
    title: "Provenance",
    description: "Transparent tracking solutions providing traceability and verification for products, supply chains, and assets.",
  },
  {
    icon: <DollarSign className="h-8 w-8 text-primary" />,
    href: "/api-platform-monetization",
    title: "API & Platform Monetization",
    description: "Micropayments Architecture Services designed to grow your business, expand your reach, and reduce friction.",
  },
  {
    icon: <Code className="h-8 w-8 text-primary" />,
    href: "/smart-contract-development",
    title: "Smart Contract Development",
    description: "Let us do the heavy lifting so you can focus on building more efficient interactions.",
  },
  {
    icon: <Dice5 className="h-8 w-8 text-primary" />,
    href: "/provably-fair-gaming-architecture",
    title: "Provably Fair Gaming Architecture Development Solutions",
    description: "Games should be fair, and with blockchain, they can be. Let us design solutions that empower players with greater trust and transparency.",
  },
  {
    icon: <Network className="h-8 w-8 text-primary" />,
    href: "/blockchain-integration-consultancy",
    title: "Blockchain Integration / Network Architecture consultancy",
    description: "Pioneering research into scalable, resilient base layer systems backed by world class expertise.",
  }
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

const Features = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section id="features" className="py-20 bg-muted/30 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
      <div className="absolute -right-32 bottom-32 h-96 w-96 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute -left-32 top-32 h-96 w-96 bg-primary/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        <div className="text-center mb-16">
          <span className="inline-flex items-center px-3 py-1 text-sm rounded-full bg-primary/10 text-primary mb-4">
            Core Solutions & Services
          </span>
          <h2 className="text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary to-blue-500">Empowering Your Metanet Journey</h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Explore our comprehensive suite of solutions designed to help you leverage the power of the Metanet.
          </p>
        </div>

        <motion.div
          ref={ref}
          variants={container}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {solutions.map((solution, index) => (
            <motion.div
              key={index}
              variants={item}
              className="bg-card border rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:translate-y-[-5px] flex flex-col"
              whileHover={{ scale: 1.02 }}
            >
              <div className="mb-4 bg-primary/10 w-14 h-14 rounded-lg flex items-center justify-center shrink-0">
                {solution.icon}
              </div>
              <div className="flex-grow">
                <h3 className="text-xl font-semibold mb-2">{solution.title}</h3>
                <p className="text-muted-foreground mb-6">{solution.description}</p>
              </div>
              <Link 
                to={solution.href} 
                className={cn(
                  buttonVariants({ variant: "ghost" }), 
                  "text-primary mt-auto self-start"
                )}
              >
                Learn more →
              </Link>
            </motion.div>
          ))}
        </motion.div>

        <div className="mt-16 text-center flex flex-col sm:flex-row justify-center items-center gap-4">
          <Link
            to="/contact"
            className={cn(buttonVariants({ size: "lg" }), "group")}
          >
            Discuss Your Project
            <motion.span
              className="ml-2"
              initial={{ x: 0 }}
              animate={{ x: [0, 5, 0] }}
              transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
            >
              →
            </motion.span>
          </Link>

          <a
            href="https://docs.projectbabbage.com/docs/concepts"
            target="_blank"
            rel="noopener noreferrer"
            className={cn(buttonVariants({ variant: "outline", size: "lg" }), "group")}
          >
            Learn More
            <BookOpen className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Features;
